import { ADUModel, PoolModel, DesignObject } from '../../store/designStore'

// Design style categories
export interface DesignStyle {
  id: string
  name: string
  description: string
  characteristics: string[]
  colorPalette: {
    primary: string[]
    secondary: string[]
    accent: string[]
  }
  materials: {
    preferred: string[]
    avoid: string[]
  }
  features: {
    recommended: string[]
    optional: string[]
  }
  priceRange: 'budget' | 'mid-range' | 'premium' | 'luxury'
}

// Design suggestion types
export interface DesignSuggestion {
  id: string
  type: 'style_match' | 'material_upgrade' | 'feature_addition' | 'color_harmony' | 'budget_optimization'
  priority: 'high' | 'medium' | 'low'
  title: string
  description: string
  reasoning: string
  impact: {
    aesthetic: number // 0-1
    functionality: number // 0-1
    cost: number // cost impact in dollars
    timeline: number // days added to project
  }
  beforeAfter?: {
    before: any
    after: any
  }
  implementation: {
    difficulty: 'easy' | 'moderate' | 'complex'
    timeRequired: string
    skillsRequired: string[]
  }
}

// Property analysis result
export interface PropertyAnalysis {
  existingStyle: string
  architecturalPeriod: string
  colorScheme: string[]
  landscapeStyle: string
  neighborhood: {
    averageHomeValue: number
    commonStyles: string[]
    trendingFeatures: string[]
  }
  recommendations: {
    complementaryStyles: string[]
    avoidStyles: string[]
    keyConsiderations: string[]
  }
}

// Design styles database
export const DESIGN_STYLES: Record<string, DesignStyle> = {
  modern: {
    id: 'modern',
    name: 'Modern Contemporary',
    description: 'Clean lines, minimalist aesthetic, emphasis on function and simplicity',
    characteristics: ['Clean lines', 'Large windows', 'Open floor plans', 'Minimal ornamentation', 'Geometric shapes'],
    colorPalette: {
      primary: ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
      secondary: ['#2C2C2C', '#404040', '#606060'],
      accent: ['#FF6B35', '#4ECDC4', '#45B7D1']
    },
    materials: {
      preferred: ['stainless_steel', 'clear_glass', 'smooth_concrete', 'brushed_aluminum'],
      avoid: ['ornate_wood', 'decorative_stone', 'traditional_brick']
    },
    features: {
      recommended: ['Large windows', 'Flat or low-slope roof', 'Open concept', 'Smart home integration'],
      optional: ['Green roof', 'Solar panels', 'Minimalist landscaping']
    },
    priceRange: 'premium'
  },
  traditional: {
    id: 'traditional',
    name: 'Traditional Classic',
    description: 'Timeless design with classic proportions and familiar materials',
    characteristics: ['Symmetrical facade', 'Pitched roofs', 'Traditional materials', 'Detailed trim work'],
    colorPalette: {
      primary: ['#F8F6F0', '#E8E2D5', '#D4C4A8'],
      secondary: ['#8B4513', '#654321', '#A0522D'],
      accent: ['#2F4F4F', '#556B2F', '#8B0000']
    },
    materials: {
      preferred: ['cedar_siding', 'clay_tiles', 'granite', 'oak_flooring'],
      avoid: ['metal_roofing', 'stainless_steel', 'ultra_modern_glass']
    },
    features: {
      recommended: ['Gabled roof', 'Front porch', 'Traditional windows', 'Classic proportions'],
      optional: ['Shutters', 'Dormer windows', 'Brick chimney']
    },
    priceRange: 'mid-range'
  },
  craftsman: {
    id: 'craftsman',
    name: 'Craftsman Bungalow',
    description: 'Handcrafted details, natural materials, and cozy proportions',
    characteristics: ['Exposed rafters', 'Built-in furniture', 'Natural materials', 'Handcrafted details'],
    colorPalette: {
      primary: ['#DEB887', '#D2B48C', '#F5DEB3'],
      secondary: ['#8B4513', '#A0522D', '#CD853F'],
      accent: ['#2F4F4F', '#556B2F', '#B22222']
    },
    materials: {
      preferred: ['cedar_siding', 'pine_trim', 'oak_flooring', 'travertine'],
      avoid: ['stainless_steel', 'ultra_modern_materials', 'synthetic_siding']
    },
    features: {
      recommended: ['Covered porch', 'Exposed beams', 'Built-in storage', 'Natural wood trim'],
      optional: ['Stone foundation', 'Dormer windows', 'Tapered columns']
    },
    priceRange: 'mid-range'
  },
  mediterranean: {
    id: 'mediterranean',
    name: 'Mediterranean Villa',
    description: 'Warm colors, stucco walls, and tile roofs inspired by coastal Europe',
    characteristics: ['Stucco exterior', 'Red tile roof', 'Arched openings', 'Wrought iron details'],
    colorPalette: {
      primary: ['#F5E6D3', '#E6D7C3', '#D4C4A8'],
      secondary: ['#CD853F', '#D2691E', '#A0522D'],
      accent: ['#4682B4', '#5F9EA0', '#2E8B57']
    },
    materials: {
      preferred: ['clay_tiles', 'travertine', 'limestone', 'copper_patina'],
      avoid: ['metal_siding', 'asphalt_shingles', 'modern_glass']
    },
    features: {
      recommended: ['Tile roof', 'Stucco walls', 'Arched windows', 'Outdoor living spaces'],
      optional: ['Courtyard', 'Fountain', 'Wrought iron details']
    },
    priceRange: 'premium'
  },
  farmhouse: {
    id: 'farmhouse',
    name: 'Modern Farmhouse',
    description: 'Rustic charm meets contemporary comfort with board-and-batten siding',
    characteristics: ['Board and batten siding', 'Metal roof', 'Large front porch', 'Barn-style doors'],
    colorPalette: {
      primary: ['#FFFFFF', '#F8F8FF', '#F5F5DC'],
      secondary: ['#2F2F2F', '#404040', '#696969'],
      accent: ['#8B4513', '#A0522D', '#2F4F4F']
    },
    materials: {
      preferred: ['cedar_siding', 'metal_roofing', 'pine_trim', 'exposed_aggregate'],
      avoid: ['stucco', 'clay_tiles', 'ultra_modern_materials']
    },
    features: {
      recommended: ['Large porch', 'Metal roof', 'Barn doors', 'Shiplap interior'],
      optional: ['Cupola', 'Weather vane', 'Split-rail fencing']
    },
    priceRange: 'mid-range'
  },
  industrial: {
    id: 'industrial',
    name: 'Industrial Loft',
    description: 'Raw materials, exposed structure, and urban aesthetic',
    characteristics: ['Exposed steel', 'Concrete floors', 'Large windows', 'Raw materials'],
    colorPalette: {
      primary: ['#A8A8A8', '#C0C0C0', '#E5E5E5'],
      secondary: ['#2C2C2C', '#404040', '#1C1C1C'],
      accent: ['#FF6B35', '#F39C12', '#E74C3C']
    },
    materials: {
      preferred: ['brushed_aluminum', 'smooth_concrete', 'stainless_steel', 'clear_glass'],
      avoid: ['traditional_wood', 'decorative_elements', 'soft_materials']
    },
    features: {
      recommended: ['Exposed structure', 'Large windows', 'Concrete floors', 'Metal accents'],
      optional: ['Skylights', 'Industrial lighting', 'Polished concrete']
    },
    priceRange: 'premium'
  }
}

export class DesignIntelligence {
  private existingObjects: DesignObject[]
  private propertyAnalysis: PropertyAnalysis

  constructor(existingObjects: DesignObject[], propertyAnalysis?: PropertyAnalysis) {
    this.existingObjects = existingObjects
    this.propertyAnalysis = propertyAnalysis || this.generateDefaultPropertyAnalysis()
  }

  // Main design analysis and suggestion engine
  generateDesignSuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []

    // Style matching suggestions
    suggestions.push(...this.generateStyleMatchingSuggestions(targetObject))
    
    // Material upgrade suggestions
    suggestions.push(...this.generateMaterialUpgradeSuggestions(targetObject))
    
    // Feature addition suggestions
    suggestions.push(...this.generateFeatureAdditionSuggestions(targetObject))
    
    // Color harmony suggestions
    suggestions.push(...this.generateColorHarmonySuggestions(targetObject))
    
    // Budget optimization suggestions
    suggestions.push(...this.generateBudgetOptimizationSuggestions(targetObject))

    // Sort by priority and impact
    return suggestions
      .sort((a, b) => {
        const priorityWeight = { high: 3, medium: 2, low: 1 }
        const aPriority = priorityWeight[a.priority]
        const bPriority = priorityWeight[b.priority]
        
        if (aPriority !== bPriority) return bPriority - aPriority
        
        // Secondary sort by aesthetic impact
        return b.impact.aesthetic - a.impact.aesthetic
      })
      .slice(0, 15) // Top 15 suggestions
  }

  // Analyze existing property style
  analyzeExistingStyle(): { detectedStyle: string; confidence: number; reasoning: string } {
    // Simplified style detection based on materials and features
    const styleScores: Record<string, number> = {}
    
    Object.keys(DESIGN_STYLES).forEach(styleId => {
      styleScores[styleId] = 0
    })

    // Analyze existing objects for style clues
    this.existingObjects.forEach(obj => {
      if (obj.type === 'ADU') {
        const adu = obj as ADUModel
        
        // Check materials
        Object.entries(DESIGN_STYLES).forEach(([styleId, style]) => {
          const materialMatch = this.calculateMaterialStyleMatch(adu.materials, style)
          styleScores[styleId] += materialMatch * 0.4
        })
        
        // Check features
        Object.entries(DESIGN_STYLES).forEach(([styleId, style]) => {
          const featureMatch = this.calculateFeatureStyleMatch(adu.features, style)
          styleScores[styleId] += featureMatch * 0.3
        })
      }
    })

    // Find highest scoring style
    const sortedStyles = Object.entries(styleScores)
      .sort(([,a], [,b]) => b - a)
    
    const [detectedStyle, score] = sortedStyles[0]
    const confidence = Math.min(score, 1)
    
    const reasoning = `Detected ${DESIGN_STYLES[detectedStyle]?.name || detectedStyle} style based on material choices and architectural features. Confidence: ${Math.round(confidence * 100)}%`
    
    return { detectedStyle, confidence, reasoning }
  }

  // Generate style matching suggestions
  private generateStyleMatchingSuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []
    const { detectedStyle, confidence } = this.analyzeExistingStyle()
    
    if (confidence < 0.7) {
      // Suggest establishing a clear style
      const recommendedStyles = this.propertyAnalysis.recommendations.complementaryStyles
      
      recommendedStyles.forEach(styleId => {
        const style = DESIGN_STYLES[styleId]
        if (style) {
          suggestions.push({
            id: `style-establish-${styleId}`,
            type: 'style_match',
            priority: 'high',
            title: `Establish ${style.name} Style`,
            description: `Create a cohesive ${style.name.toLowerCase()} aesthetic throughout your property`,
            reasoning: `Your current design lacks a clear style direction. ${style.name} would complement your property's architecture and neighborhood character.`,
            impact: {
              aesthetic: 0.9,
              functionality: 0.3,
              cost: this.estimateStyleImplementationCost(style),
              timeline: 14
            },
            implementation: {
              difficulty: 'moderate',
              timeRequired: '2-3 weeks',
              skillsRequired: ['Design consultation', 'Material selection', 'Color coordination']
            }
          })
        }
      })
    } else {
      // Suggest reinforcing existing style
      const currentStyle = DESIGN_STYLES[detectedStyle]
      if (currentStyle) {
        suggestions.push({
          id: `style-reinforce-${detectedStyle}`,
          type: 'style_match',
          priority: 'medium',
          title: `Enhance ${currentStyle.name} Elements`,
          description: `Strengthen your ${currentStyle.name.toLowerCase()} design with additional characteristic features`,
          reasoning: `You have a good foundation in ${currentStyle.name} style. Adding key characteristic elements would create a more cohesive and polished look.`,
          impact: {
            aesthetic: 0.7,
            functionality: 0.2,
            cost: this.estimateStyleEnhancementCost(currentStyle),
            timeline: 7
          },
          implementation: {
            difficulty: 'easy',
            timeRequired: '1 week',
            skillsRequired: ['Material selection', 'Detail work']
          }
        })
      }
    }

    return suggestions
  }

  // Generate material upgrade suggestions
  private generateMaterialUpgradeSuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []
    
    this.existingObjects.forEach((obj, index) => {
      if (obj.type === 'ADU') {
        const adu = obj as ADUModel
        const { detectedStyle } = this.analyzeExistingStyle()
        const style = DESIGN_STYLES[detectedStyle]
        
        if (style) {
          // Check for material upgrades
          const materialUpgrades = this.identifyMaterialUpgrades(adu.materials, style)
          
          materialUpgrades.forEach(upgrade => {
            suggestions.push({
              id: `material-upgrade-${index}-${upgrade.type}`,
              type: 'material_upgrade',
              priority: upgrade.impact > 0.7 ? 'high' : 'medium',
              title: `Upgrade ${upgrade.type.replace('_', ' ')} Material`,
              description: upgrade.description,
              reasoning: upgrade.reasoning,
              impact: {
                aesthetic: upgrade.impact,
                functionality: upgrade.functionalImpact,
                cost: upgrade.cost,
                timeline: upgrade.timeline
              },
              beforeAfter: {
                before: upgrade.currentMaterial,
                after: upgrade.suggestedMaterial
              },
              implementation: {
                difficulty: upgrade.difficulty,
                timeRequired: upgrade.timeRequired,
                skillsRequired: upgrade.skillsRequired
              }
            })
          })
        }
      }
    })

    return suggestions
  }

  // Generate feature addition suggestions
  private generateFeatureAdditionSuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []
    
    this.existingObjects.forEach((obj, index) => {
      if (obj.type === 'ADU') {
        const adu = obj as ADUModel
        
        // Suggest missing essential features
        if (!adu.features.storage) {
          suggestions.push({
            id: `feature-storage-${index}`,
            type: 'feature_addition',
            priority: 'high',
            title: 'Add Built-in Storage',
            description: 'Include built-in storage solutions to maximize space efficiency',
            reasoning: 'ADUs benefit greatly from built-in storage to make the most of limited space. This is especially important for smaller units.',
            impact: {
              aesthetic: 0.6,
              functionality: 0.9,
              cost: 2500,
              timeline: 5
            },
            implementation: {
              difficulty: 'moderate',
              timeRequired: '3-5 days',
              skillsRequired: ['Carpentry', 'Design planning']
            }
          })
        }
        
        if (!adu.features.porch && adu.dimensions.width * adu.dimensions.depth > 600) {
          suggestions.push({
            id: `feature-porch-${index}`,
            type: 'feature_addition',
            priority: 'medium',
            title: 'Add Covered Porch',
            description: 'Include a covered porch to create outdoor living space and enhance curb appeal',
            reasoning: 'A covered porch adds significant value and livability to an ADU, providing transition space and weather protection.',
            impact: {
              aesthetic: 0.8,
              functionality: 0.7,
              cost: 4000,
              timeline: 10
            },
            implementation: {
              difficulty: 'moderate',
              timeRequired: '1-2 weeks',
              skillsRequired: ['Framing', 'Roofing', 'Foundation work']
            }
          })
        }
      }
      
      if (obj.type === 'Pool') {
        const pool = obj as PoolModel
        
        if (!pool.features.lighting) {
          suggestions.push({
            id: `feature-pool-lighting-${index}`,
            type: 'feature_addition',
            priority: 'high',
            title: 'Add Pool Lighting',
            description: 'Install LED pool lighting for safety, ambiance, and extended use',
            reasoning: 'Pool lighting is essential for safety and dramatically enhances the pool experience during evening hours.',
            impact: {
              aesthetic: 0.8,
              functionality: 0.9,
              cost: 1500,
              timeline: 3
            },
            implementation: {
              difficulty: 'moderate',
              timeRequired: '2-3 days',
              skillsRequired: ['Electrical work', 'Pool equipment installation']
            }
          })
        }
        
        if (!pool.features.heating && pool.dimensions.width * (pool.dimensions as any).length > 400) {
          suggestions.push({
            id: `feature-pool-heating-${index}`,
            type: 'feature_addition',
            priority: 'medium',
            title: 'Add Pool Heating System',
            description: 'Install a heating system to extend swimming season and comfort',
            reasoning: 'Pool heating significantly extends the usable season and adds substantial value to larger pools.',
            impact: {
              aesthetic: 0.3,
              functionality: 0.8,
              cost: 3500,
              timeline: 7
            },
            implementation: {
              difficulty: 'complex',
              timeRequired: '1 week',
              skillsRequired: ['Plumbing', 'Gas/electrical work', 'Pool equipment']
            }
          })
        }
      }
    })

    return suggestions
  }

  // Generate color harmony suggestions
  private generateColorHarmonySuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []
    const { detectedStyle } = this.analyzeExistingStyle()
    const style = DESIGN_STYLES[detectedStyle]
    
    if (style) {
      this.existingObjects.forEach((obj, index) => {
        if (obj.type === 'ADU') {
          const adu = obj as ADUModel
          const colorHarmonyScore = this.calculateColorHarmony(adu.materials, style.colorPalette)
          
          if (colorHarmonyScore < 0.6) {
            suggestions.push({
              id: `color-harmony-${index}`,
              type: 'color_harmony',
              priority: 'medium',
              title: 'Improve Color Coordination',
              description: `Adjust color scheme to better match ${style.name} palette`,
              reasoning: `Current colors don't fully complement the ${style.name} style. A more coordinated palette would enhance the overall aesthetic.`,
              impact: {
                aesthetic: 0.7,
                functionality: 0.1,
                cost: 800, // Paint and minor material changes
                timeline: 3
              },
              implementation: {
                difficulty: 'easy',
                timeRequired: '2-3 days',
                skillsRequired: ['Color consultation', 'Painting']
              }
            })
          }
        }
      })
    }

    return suggestions
  }

  // Generate budget optimization suggestions
  private generateBudgetOptimizationSuggestions(targetObject?: DesignObject): DesignSuggestion[] {
    const suggestions: DesignSuggestion[] = []
    
    // Suggest cost-effective alternatives
    this.existingObjects.forEach((obj, index) => {
      if (obj.type === 'ADU') {
        const adu = obj as ADUModel
        
        // Look for expensive materials that could be substituted
        const costSavingOpportunities = this.identifyCostSavingOpportunities(adu)
        
        costSavingOpportunities.forEach(opportunity => {
          suggestions.push({
            id: `budget-optimize-${index}-${opportunity.type}`,
            type: 'budget_optimization',
            priority: 'low',
            title: opportunity.title,
            description: opportunity.description,
            reasoning: opportunity.reasoning,
            impact: {
              aesthetic: opportunity.aestheticImpact,
              functionality: opportunity.functionalImpact,
              cost: -opportunity.savings, // Negative cost = savings
              timeline: 0
            },
            implementation: {
              difficulty: 'easy',
              timeRequired: 'No additional time',
              skillsRequired: ['Material selection']
            }
          })
        })
      }
    })

    return suggestions
  }

  // Helper methods
  private calculateMaterialStyleMatch(materials: any, style: DesignStyle): number {
    let score = 0
    let totalChecks = 0

    Object.values(materials).forEach(material => {
      totalChecks++
      if (style.materials.preferred.includes(material as string)) {
        score += 1
      } else if (style.materials.avoid.includes(material as string)) {
        score -= 0.5
      }
    })

    return totalChecks > 0 ? Math.max(0, score / totalChecks) : 0
  }

  private calculateFeatureStyleMatch(features: any, style: DesignStyle): number {
    let score = 0
    let totalChecks = 0

    // This would be more sophisticated in a real implementation
    // For now, just check if features align with style characteristics
    if (features.porch && style.characteristics.includes('Front porch')) {
      score += 0.3
    }
    if (features.fireplace && style.characteristics.includes('Traditional materials')) {
      score += 0.2
    }
    
    totalChecks = 1
    return score
  }

  private calculateColorHarmony(materials: any, colorPalette: any): number {
    // Simplified color harmony calculation
    // In a real implementation, this would analyze actual color values
    return Math.random() * 0.4 + 0.4 // Random score between 0.4-0.8 for demo
  }

  private identifyMaterialUpgrades(materials: any, style: DesignStyle): any[] {
    const upgrades: any[] = []
    
    // Example upgrade identification
    if (materials.walls === '#f5f5f5' && style.materials.preferred.includes('cedar_siding')) {
      upgrades.push({
        type: 'walls',
        currentMaterial: materials.walls,
        suggestedMaterial: 'cedar_siding',
        description: 'Upgrade to cedar siding for better style alignment',
        reasoning: 'Cedar siding would better complement the detected architectural style',
        impact: 0.8,
        functionalImpact: 0.3,
        cost: 5000,
        timeline: 10,
        difficulty: 'moderate',
        timeRequired: '1-2 weeks',
        skillsRequired: ['Siding installation', 'Exterior finishing']
      })
    }

    return upgrades
  }

  private identifyCostSavingOpportunities(adu: ADUModel): any[] {
    const opportunities: any[] = []
    
    // Example cost-saving opportunity
    if (adu.materials.roof === '#654321') { // Expensive slate
      opportunities.push({
        type: 'roof',
        title: 'Consider Architectural Shingles',
        description: 'Switch from slate to high-quality architectural shingles',
        reasoning: 'Architectural shingles provide similar aesthetic appeal at 60% less cost',
        savings: 8000,
        aestheticImpact: -0.2,
        functionalImpact: 0
      })
    }

    return opportunities
  }

  private estimateStyleImplementationCost(style: DesignStyle): number {
    const baseCosts: Record<string, number> = {
      budget: 3000,
      'mid-range': 6000,
      premium: 12000,
      luxury: 20000
    }
    return baseCosts[style.priceRange] || 6000
  }

  private estimateStyleEnhancementCost(style: DesignStyle): number {
    return this.estimateStyleImplementationCost(style) * 0.3 // 30% of full implementation
  }

  private generateDefaultPropertyAnalysis(): PropertyAnalysis {
    return {
      existingStyle: 'traditional',
      architecturalPeriod: '1990s',
      colorScheme: ['#F8F6F0', '#8B4513', '#2F4F4F'],
      landscapeStyle: 'suburban',
      neighborhood: {
        averageHomeValue: 450000,
        commonStyles: ['traditional', 'craftsman', 'farmhouse'],
        trendingFeatures: ['outdoor living', 'smart home', 'energy efficiency']
      },
      recommendations: {
        complementaryStyles: ['traditional', 'craftsman', 'farmhouse'],
        avoidStyles: ['ultra-modern', 'industrial'],
        keyConsiderations: ['Neighborhood compatibility', 'Resale value', 'Maintenance requirements']
      }
    }
  }
}

export default DesignIntelligence
